/**
 * Utility functions for managing quiz responses in localStorage
 * Handles saving, loading, and clearing quiz data
 */

const QUIZ_STORAGE_KEY = 'pawfect_match_quiz_responses';
const QUIZ_TIMESTAMP_KEY = 'pawfect_match_quiz_timestamp';

/**
 * Save quiz responses to localStorage
 * @param {Object} responses - Quiz responses object
 */
export const saveQuizResponses = (responses) => {
  try {
    const timestamp = new Date().toISOString();
    localStorage.setItem(QUIZ_STORAGE_KEY, JSON.stringify(responses));
    localStorage.setItem(QUIZ_TIMESTAMP_KEY, timestamp);
  } catch (error) {
    console.warn('Failed to save quiz responses to localStorage:', error);
  }
};

/**
 * Load quiz responses from localStorage
 * @returns {Object|null} Quiz responses or null if none exist
 */
export const loadQuizResponses = () => {
  try {
    const responses = localStorage.getItem(QUIZ_STORAGE_KEY);
    return responses ? JSON.parse(responses) : null;
  } catch (error) {
    console.warn('Failed to load quiz responses from localStorage:', error);
    return null;
  }
};

/**
 * Get the timestamp of when quiz was last completed
 * @returns {string|null} ISO timestamp or null if none exists
 */
export const getQuizTimestamp = () => {
  try {
    return localStorage.getItem(QUIZ_TIMESTAMP_KEY);
  } catch (error) {
    console.warn('Failed to load quiz timestamp from localStorage:', error);
    return null;
  }
};

/**
 * Clear all quiz data from localStorage
 */
export const clearQuizData = () => {
  try {
    localStorage.removeItem(QUIZ_STORAGE_KEY);
    localStorage.removeItem(QUIZ_TIMESTAMP_KEY);
  } catch (error) {
    console.warn('Failed to clear quiz data from localStorage:', error);
  }
};

/**
 * Check if quiz has been completed (has saved responses)
 * @returns {boolean} True if quiz has been completed
 */
export const hasCompletedQuiz = () => {
  const responses = loadQuizResponses();
  return responses !== null && Object.keys(responses).length > 0;
};

/**
 * Get quiz completion summary
 * @returns {Object} Summary with completion status, timestamp, and response count
 */
export const getQuizSummary = () => {
  const responses = loadQuizResponses();
  const timestamp = getQuizTimestamp();
  
  return {
    isCompleted: responses !== null,
    responseCount: responses ? Object.keys(responses).length : 0,
    lastCompleted: timestamp,
    responses: responses,
  };
};